import { useState } from "react";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { WarningCircle } from "@phosphor-icons/react";

const DECISIONS = [
  { key: "approve", label: "Approve", testid: "approve", cls: "bg-[#10B981] hover:bg-[#059669]" },
  { key: "decline", label: "Decline", testid: "decline", cls: "bg-[#EF4444] hover:bg-[#DC2626]" },
  { key: "more_info", label: "Need more info", testid: "more-info", cls: "bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A]" },
];

const Row = ({ r, reload }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/portal/requests/${r.id}/decide`, { decision, note: note.trim() });
      toast.success("Decision recorded and the family has been told");
      setNote("");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const addInternal = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/portal/requests/${r.id}/reply`, { note: note.trim(), internal: true });
      toast.success("Internal note added — the family cannot see it");
      setNote("");
      await reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid={`queue-request-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold">
            {r.type_label}
            {r.stale && (
              <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs font-bold"
                    data-testid={`stale-${r.id}`}>
                <WarningCircle size={13} weight="bold" /> Waiting too long
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {r.student_name || "General"}{r.room ? ` · ${r.room}` : ""} · from {r.parent_name}
            {r.requested_date ? ` · ${r.requested_date}` : ""}{r.requested_time ? ` at ${r.requested_time}` : ""}
          </p>
        </div>
        <span className="rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-3 py-1 text-xs font-bold">
          {r.status_label}
        </span>
      </div>

      {r.details && <p className="mt-3 text-sm">{r.details}</p>}
      {r.person_name && (
        <p className="mt-2 text-sm text-muted-foreground">
          Person: <strong>{r.person_name}</strong>
          {r.person_relationship ? ` · ${r.person_relationship}` : ""}
          {r.person_phone ? ` · ${r.person_phone}` : ""}
        </p>
      )}
      {r.health_review && (
        <p className="mt-2 text-xs font-bold text-amber-700">
          Also queued for health review — change the care plan there, not here.
        </p>
      )}

      <ol className="mt-4 space-y-2 border-l-2 border-border pl-4 text-sm">
        {r.trail.map((t, i) => (
          <li key={`${t.at}-${i}`}>
            <p className={t.internal ? "font-bold text-amber-700" : "font-bold"}>
              {t.internal ? "Internal · " : ""}{t.note || "Status updated"}
            </p>
            <p className="text-xs text-muted-foreground">{t.by} · {dateTimeOf(t.at)}</p>
          </li>
        ))}
      </ol>

      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Note for the family (required to decline or ask for more information)"
                className="mt-4 rounded-2xl" data-testid={`decide-note-${r.id}`} />
      <div className="mt-3 flex flex-wrap gap-2">
        {DECISIONS.map((d) => (
          <Button key={d.key} size="sm" disabled={busy} onClick={() => decide(d.key)}
                  className={`rounded-full font-bold text-white ${d.cls}`}
                  data-testid={`${d.testid}-${r.id}`}>
            {d.label}
          </Button>
        ))}
        <Button size="sm" variant="outline" disabled={busy} onClick={addInternal}
                className="rounded-full font-bold" data-testid={`internal-note-${r.id}`}>
          Internal note
        </Button>
      </div>
    </div>
  );
};

export default function QueueTab({ queue, reload }) {
  if (!queue) return <p className="text-muted-foreground" data-testid="queue-loading">Loading requests…</p>;
  if (queue.requests.length === 0)
    return (
      <p className="text-sm text-muted-foreground" data-testid="queue-empty">
        Nothing is waiting on you — every family request has been decided.
      </p>
    );
  return (
    <div className="space-y-4" data-testid="queue-list">
      {queue.requests.map((r) => <Row key={r.id} r={r} reload={reload} />)}
    </div>
  );
}
